import React, { useState } from 'react'
import { Table } from 'shared/ui/Table/Table'
import { useGeneratedColumns, useSelectColumn } from './columns'
import { Box, Button } from '@mui/material'
import { useLeafUpdate, useNodeUpdate, useUpdateAll } from './hooks'
import { useListSwap } from 'shared/hooks'
import { ConfirmCommitButton } from 'shared/ui'
import { SelectedUrl } from './SelectedUrl'
import { urlSortAtom, urlsWithAvailableAltText } from './atoms'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { InfoDisplay } from '../../utility/Containers'

// UpdateGroup - shows you all images that can be updated with generated alt text.
// You can include/exclude any subset of images to be updated.
// You can select images to see more information about the image data.
export function UpdateGroup() {
  const [selected, setSelected] = useState('')

  const { toInclude, toExclude, onExclude, onInclude, includeAll, excludeAll } =
    useUpdateGroupLists()

  const onLeafUpdate = useLeafUpdate()
  const onNodeUpdate = useNodeUpdate()

  return (
    <Box>
      <Info />
      <Box>
        <SelectedUrl
          url={selected}
          onDeselect={() => setSelected('')}
          leafSelectFunc={onLeafUpdate}
          nodeSelectFunc={onNodeUpdate}
        />
      </Box>
      <UpdateItemsButton items={toInclude} />
      <IncludeExcludeTable
        groupType={'Include'}
        groupAction={excludeAll}
        items={toInclude}
        itemClick={onExclude}
        onSelect={setSelected}
      />
      <IncludeExcludeTable
        groupType={'Exclude'}
        groupAction={includeAll}
        items={toExclude}
        itemClick={onInclude}
        onSelect={setSelected}
      />
    </Box>
  )
}

function UpdateItemsButton({ items }: { items: string[] }) {
  const updateAllFunc = useUpdateAll()
  function doUpdateAll() {
    return updateAllFunc(items)
  }

  return (
    <ConfirmCommitButton disabled={!items.length} onClick={doUpdateAll}>
      Update All {items.length}
    </ConfirmCommitButton>
  )
}

function IncludeExcludeTable({
  groupType,
  groupAction,
  itemClick,
  items,
  onSelect,
}: {
  groupType: 'Include' | 'Exclude'
  groupAction(): void
  items: string[]
  itemClick(item: string): void
  onSelect(item: string): void
}) {
  const oppositeAction = groupType === 'Include' ? 'Exclude' : 'Include'

  const incl = useGeneratedColumns(itemClick, oppositeAction)
  const selectCol = useSelectColumn('Select', onSelect, 'SEL')

  return (
    <Box padding="10px">
      <Button disabled={!items.length} onClick={groupAction}>
        {oppositeAction} All
      </Button>
      <Table columns={[...incl, selectCol]} items={items} />
    </Box>
  )
}

function useUpdateGroupLists() {
  const allUrls = useAtomValue(urlsWithAvailableAltText)
  const urlSort = useAtomValue(urlSortAtom)
  return useListSwap({
    items: allUrls,
    sort: urlSort,
  })
}

function Info() {
  return (
    <InfoDisplay>
      <Box>
        This component presents all the updates that are currently available
        based on the source alt text and the images missing alt text.
      </Box>
      <Box>
        You can Update All or you can exclude images from being updated.
      </Box>
    </InfoDisplay>
  )
}
